import React, { useEffect, useState } from "react";
import { api, resolveImage } from "../lib/api";
import { Plus } from "lucide-react";
import { useCart } from "../context/CartContext";

export const ProductGrid = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/products");
        // Hide sold-out products from public page? We'll show them but disable add-to-cart with "Sold Out" badge.
        setProducts(res.data);
      } catch (e) {
        console.error("Failed to load products", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <section
      id="ready-to-ship"
      className="w-full py-16 md:py-24 px-6"
      style={{ backgroundColor: "var(--bg-primary)" }}
      data-testid="ready-to-ship-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p
            className="font-script text-2xl md:text-3xl mb-2"
            style={{ color: "var(--text-accent)" }}
          >
            freshly made &amp; ready to cuddle
          </p>
          <h2
            className="font-display text-4xl md:text-5xl lg:text-6xl"
            style={{ color: "var(--text-primary)" }}
          >
            Ready To Ship
          </h2>
        </div>

        {loading ? (
          <div
            className="text-center font-script text-2xl"
            style={{ color: "var(--text-secondary)" }}
            data-testid="products-loading"
          >
            stitching the catalog together…
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {products.map((p) => {
              const soldOut = p.in_stock === false;
              const lowStock = p.stock_count != null && p.stock_count <= 3 && p.stock_count > 0;
              return (
                <article
                  key={p.id}
                  className="product-card bg-white rounded-2xl overflow-hidden"
                  style={{ border: "1px solid var(--border-soft)" }}
                  data-testid={`product-card-${p.id}`}
                >
                  <div className="relative aspect-square overflow-hidden bg-[var(--bg-secondary)]">
                    <img
                      src={resolveImage(p.image_url)}
                      alt={p.name}
                      className="w-full h-full object-cover"
                      style={{ filter: soldOut ? "grayscale(0.8)" : "none" }}
                      loading="lazy"
                    />
                    {soldOut && (
                      <span
                        className="absolute top-3 left-3 text-[10px] uppercase tracking-wider font-semibold px-3 py-1 rounded-full"
                        style={{ backgroundColor: "#fff", color: "var(--brand-orange)", border: "1px solid var(--brand-orange)" }}
                        data-testid={`sold-out-badge-${p.id}`}
                      >
                        Sold Out
                      </span>
                    )}
                    {!soldOut && lowStock && (
                      <span
                        className="absolute top-3 left-3 text-[10px] uppercase tracking-wider font-semibold px-3 py-1 rounded-full"
                        style={{ backgroundColor: "var(--brand-orange)", color: "#fff" }}
                        data-testid={`low-stock-badge-${p.id}`}
                      >
                        Only {p.stock_count} left
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3
                      className="font-display text-2xl mb-1 leading-tight"
                      style={{ color: "var(--text-primary)" }}
                      data-testid={`product-name-${p.id}`}
                    >
                      {p.name}
                    </h3>
                    <p
                      className="text-sm leading-relaxed mb-4 min-h-[3rem]"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {p.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span
                        className="font-display text-2xl"
                        style={{ color: "var(--brand-sage)" }}
                        data-testid={`product-price-${p.id}`}
                      >
                        ${Number(p.price).toFixed(2)}
                      </span>
                      <button
                        onClick={() => !soldOut && addItem(p)}
                        disabled={soldOut}
                        className="btn-sage flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`add-to-cart-button-${p.id}`}
                      >
                        <Plus size={16} />
                        {soldOut ? "Sold" : "Add"}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductGrid;
